import { ErrorModal } from "@dexteel/mesf-core";
import { CircularProgress, TextField } from "@mui/material";
import { Autocomplete } from "@mui/material";
import { get } from "lodash-es";
import { useEffect, useState } from "react";
import { FieldError } from "react-hook-form";
import { Material } from "../model/Material";
import { getMaterials } from "../repositories/MaterialRepository";

type Props = {
  label: string;
  value: number | null;
  onChange?: Function;
  fieldError: FieldError | undefined;
  disabled?: boolean;
  filter?: (material: Material) => boolean;
};

export const MaterialAutocomplete = ({
  label,
  value,
  onChange = () => {},
  fieldError,
  disabled = false,
  filter,
}: Props) => {
  const [materials, setMaterials] = useState<Material[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState("");

  const loadData = async () => {
    setIsLoading(true);
    setError("");
    const resp = await getMaterials();
    if (resp.ok) {
      const rows = get(resp, "data.tables[0].rows", []) as Material[];
      setMaterials(rows);
    } else {
      setError(resp.message);
    }
    setIsLoading(false);
  };

  const selectedValue =
    materials?.find((material: Material) => material.MaterialId === value) ||
    null;

  const filteredMaterials = filter ? materials.filter(filter) : materials;

  const materialOptions =
    selectedValue &&
    !filteredMaterials.some((m) => m.MaterialId === selectedValue.MaterialId)
      ? [...filteredMaterials, selectedValue]
      : filteredMaterials;

  useEffect(() => {
    loadData().then(() => {});
    return;
  }, []);

  return (
    <>
      <Autocomplete
        id="clear-on-escape"
        clearOnEscape
        options={materialOptions ?? []}
        getOptionLabel={(option) => option.MaterialName}
        onChange={(event, newValue) => {
          onChange(newValue ? newValue.MaterialId : null);
        }}
        renderInput={(params) => (
          <>
            <TextField
              {...params}
              label={label}
              variant="outlined"
              size="small"
              fullWidth
              error={!!fieldError}
              helperText={fieldError?.message}
              disabled={disabled}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : (
                        params.InputProps.endAdornment
                      )}
                    </>
                  ),
                },
              }}
            />
          </>
        )}
        value={selectedValue}
      />
      <ErrorModal error={error} onHide={() => setError("")} />
    </>
  );
};
